import React, { createContext, useContext, useState, useEffect } from 'react';

const ActualitesContext = createContext(null);

export const useActualites = () => {
  const context = useContext(ActualitesContext);
  if (!context) {
    throw new Error('useActualites must be used within an ActualitesProvider');
  }
  return context;
};

export const ActualitesProvider = ({ children }) => {
  const [actualites, setActualites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Charger les actualités depuis localStorage au démarrage
  useEffect(() => {
    loadActualites();
  }, []);

  const loadActualites = () => {
    try {
      const stored = localStorage.getItem('moov4goods_actualites');
      if (stored) {
        setActualites(JSON.parse(stored));
      } else {
        // Actualités par défaut
        const defaultActualites = [
          {
            id: '1',
            title: "Salon Pollutec 2025",
            date: "4-7 octobre 2025",
            location: "Lyon",
            description: "Les 4, 5, 6 et 7 octobre 2025 prochain se tiendra à Lyon le salon Pollutec qui regroupe les principaux acteurs industriels et leurs partenaires (les entreprises, collectivités, les territoires) qui agissent pour la préservation de l'environnement.",
            image: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?w=800&h=600&fit=crop&q=80",
            link: null,
            createdAt: new Date().toISOString()
          },
          {
            id: '2',
            title: "Le Grand Est accompagne les projets industriels verts",
            date: "2025",
            location: "Grand Est",
            description: "La région du Grand Est dans le cadre du projet national intitulé « Territoires d'industrie » encourage les projets industriels portant sur la transition écologique. Neuf projets ont déjà bénéficié d'un appui chiffré à 5,1 millions d'euros.",
            image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=600&fit=crop&q=80",
            link: "https://france3-regions.franceinfo.fr/grand-est/champagne-ardenne/encourager-les-projets-industriels-verts-5-1-millions-d-euros-verses-par-le-grand-est-en-2025-3085675.html",
            createdAt: new Date().toISOString()
          },
          {
            id: '3',
            title: "Consultation publique pour la gestion de l'eau",
            date: "Novembre 2024 - Mai 2025",
            location: "France",
            description: "La consultation publique qui a débuté le 25 novembre 2024, pour une durée de six mois concernant la gestion de l'eau et les risques d'inondations pour la période 2028-2033.",
            image: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=800&h=600&fit=crop&q=80",
            link: "https://www.grand-est.developpement-durable.gouv.fr/consultation-publique-sur-les-enjeux-de-l-eau-et-a23007.html?lang=fr",
            createdAt: new Date().toISOString()
          }
        ];
        setActualites(defaultActualites);
        localStorage.setItem('moov4goods_actualites', JSON.stringify(defaultActualites));
      }
    } catch (error) {
      console.error('Erreur lors du chargement des actualités:', error);
    } finally {
      setLoading(false);
    }
  };

  const addActualite = (actualite) => {
    const newActualite = {
      ...actualite,
      id: Date.now().toString(),
      createdAt: new Date().toISOString()
    };
    const updated = [...actualites, newActualite];
    setActualites(updated);
    localStorage.setItem('moov4goods_actualites', JSON.stringify(updated));
    return newActualite;
  };

  const updateActualite = (id, updatedActualite) => {
    const updated = actualites.map(a => 
      a.id === id 
        ? { ...updatedActualite, id, updatedAt: new Date().toISOString() }
        : a
    );
    setActualites(updated);
    localStorage.setItem('moov4goods_actualites', JSON.stringify(updated));
  };

  const deleteActualite = (id) => {
    const updated = actualites.filter(a => a.id !== id);
    setActualites(updated);
    localStorage.setItem('moov4goods_actualites', JSON.stringify(updated));
  };

  const getActualiteById = (id) => {
    return actualites.find(a => a.id === id);
  };

  const value = {
    actualites,
    loading,
    addActualite,
    updateActualite,
    deleteActualite,
    getActualiteById,
    refreshActualites: loadActualites
  };

  return (
    <ActualitesContext.Provider value={value}>
      {children}
    </ActualitesContext.Provider>
  );
};
